import React from "react"
import { View, Text, StyleSheet } from "react-native"
import { FontAwesome5 } from "@expo/vector-icons"
import { theme } from "@common/theme/theme"
import AvatarM from "@common/components/avatar/AvatarM"

/**
 * Carte tuteur "vitrine" de l'étape aha moment ("masteurs qui correspondent").
 * Présentationnelle — reçoit un tuteur du pool curé renvoyé par le matching
 * backend : avatar (vraie photo), display_name ("Jean D."), établissement,
 * formation, les matières du programme couvertes (pills bleues) et des badges de
 * forces (ton ambré). Pas de badge "école" distinct.
 *
 * Avatar : composant AvatarM de l'app (traitement tuteur = nœud papillon +
 * bordure champion). `user.avatar` = l'URL `avatar` de la ressource (média
 * Spatie) — affichée telle quelle ; null → fallback initiale par AvatarM.
 */
const TutorShowcaseCard = ({ tutor, avatarSize = 54 }) => {
    return (
        <View style={styles.card}>
            <AvatarM
                size={avatarSize}
                user={{
                    id: tutor.id,
                    display_name: tutor.display_name,
                    avatar: tutor.avatar,
                    is_tutor: true,
                }}
                style={{ marginRight: 14 }}
            />

            <View style={styles.info}>
                <Text style={styles.name}>{tutor.display_name}</Text>

                {tutor.study_institution ? (
                    <View style={styles.metaRow}>
                        <FontAwesome5
                            name="university"
                            size={10}
                            color={theme.colors.grey}
                            style={styles.metaIcon}
                        />
                        <Text style={styles.meta} numberOfLines={1}>
                            {tutor.study_institution}
                        </Text>
                    </View>
                ) : null}

                {tutor.formation ? (
                    <View style={styles.metaRow}>
                        <FontAwesome5
                            name="graduation-cap"
                            size={10}
                            color={theme.colors.grey}
                            style={styles.metaIcon}
                        />
                        <Text style={styles.meta} numberOfLines={1}>
                            {tutor.formation}
                        </Text>
                    </View>
                ) : null}

                {(tutor.subjects || []).length > 0 ? (
                    <View style={styles.badges}>
                        {tutor.subjects.map((s) => (
                            <View key={s} style={styles.badge}>
                                <Text style={styles.badgeText}>{s}</Text>
                            </View>
                        ))}
                    </View>
                ) : null}

                {(tutor.strengths || []).length > 0 ? (
                    <View style={styles.strengthRow}>
                        {tutor.strengths.map((b) => (
                            <View key={b.label} style={styles.strengthBadge}>
                                <FontAwesome5
                                    name={b.icon}
                                    solid
                                    size={9}
                                    color="#B4690E"
                                />
                                <Text style={styles.strengthText}>
                                    {b.label}
                                </Text>
                            </View>
                        ))}
                    </View>
                ) : null}
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    card: {
        flexDirection: "row",
        alignItems: "flex-start",
        backgroundColor: theme.colors.white,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        borderRadius: 14,
        padding: 12,
        marginBottom: 10,
        // Ombre douce (web + natif).
        shadowColor: "#101d40",
        shadowOpacity: 0.06,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 2 },
        elevation: 1,
    },
    info: { flex: 1 },
    name: {
        fontFamily: "Montserrat-Bold",
        fontSize: 15,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 3,
    },
    metaRow: { flexDirection: "row", alignItems: "center", marginBottom: 2 },
    metaIcon: { marginRight: 6, width: 12 },
    meta: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
        flex: 1,
    },
    // Pills matières (bleu) — alignées sur les pills de SummerProgramCalendar.
    badges: { flexDirection: "row", flexWrap: "wrap", marginTop: 7 },
    badge: {
        backgroundColor: theme.colors.primaryLighter,
        borderRadius: 999,
        paddingVertical: 3,
        paddingHorizontal: 9,
        marginRight: 6,
        marginBottom: 4,
    },
    badgeText: {
        fontFamily: "Montserrat-Bold",
        fontSize: 11,
        color: theme.colors.primary,
    },
    // Badges de forces (crédibilité) — ton ambré, distinct des pills matières (bleu).
    strengthRow: { flexDirection: "row", flexWrap: "wrap", marginTop: 4 },
    strengthBadge: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: "#FEF3E2",
        borderRadius: 999,
        paddingVertical: 3,
        paddingHorizontal: 9,
        marginRight: 6,
        marginBottom: 4,
    },
    strengthText: {
        fontFamily: "Montserrat-Bold",
        fontSize: 11,
        color: "#B4690E",
        marginLeft: 5,
    },
})

export default TutorShowcaseCard
